import { queryOptions, type QueryClient } from "@tanstack/react-query"
import { api, ApiError } from "./api"
import { listAllData } from "./list-all"
import type { AccessibilityNeed, AllergyEntry, DietEntry } from "./dietary"

// Guest ("people") data layer for the Console collection view + the full guest
// record. Mirrors the admin GuestsPage / GuestProfilePage queries (event-scoped
// `/api/v1/guests`) and reuses the SAME cache key shapes admin uses
// (`["resource", "guests", { eventId }]`, `["guest", id]`) so the vendored
// realtime layer can patch them on live CRUD.

export type Guest = {
  id: string
  eventId: string
  organizationId: string
  linkedUserId?: string | null
  contactId?: string | null
  hostGuestId?: string | null
  relationshipToHost?: string | null
  firstName: string
  lastName?: string | null
  badgeFirstName?: string | null
  badgeLastName?: string | null
  nameAtInvite: string
  emailAtInvite: string
  phoneAtInvite?: string | null
  smsAlertsConsentAt?: string | null
  // RSVP checkboxes ticked on the in-app Confirm screen — stamped only when a
  // gated yes lands. Null = not collected on Attend (e.g. staff confirmed).
  // `rsvpCheckboxesAccepted` is a JSON string snapshot of what was shown
  // (core/rsvp-checkboxes parseRsvpAcceptance).
  rsvpCheckboxesAcceptedAt?: string | null
  rsvpCheckboxesAccepted?: string | null
  titleAtInvite?: string | null
  companyAtInvite?: string | null
  // Free-text note a public registrant leaves when requesting to join.
  responseNote?: string | null
  displayName?: string | null
  // GuestProfile uses dietaryRequests/accessibilityRequests/notes; the list
  // (GuestsPage) edits dietaryNotes/specialRequests. Keep both projections —
  // the record reads whichever the backend returns.
  dietaryNotes?: string | null
  specialRequests?: string | null
  dietaryRequests?: string | null
  accessibilityRequests?: string | null
  notes?: string | null
  preApproved: number
  status: string
  // Purchased ticket tier (paid ticketing), denormalised onto the guest by the
  // Stripe webhook. Read-only — set at purchase, never edited here. Null for
  // comped/invited guests or free events.
  ticketTypeId?: string | null
  ticketTypeName?: string | null
  guestCount: number
  maxGuests: number
  sentAt?: string | null
  respondedAt?: string | null
  checkedInAt?: string | null
  checkedOutAt?: string | null
  // Stamped by the mark-badge-printed action when a badge sheet is printed
  // for this guest; re-stamped on reprint.
  badgePrintedAt?: string | null
  // Set when an organizer explicitly sent a calendar (ICS) invite; cleared on
  // cancel-calendar-invite. The signal that a calendar invite is outstanding.
  calendarInviteSentAt?: string | null
  createdAt: string
  modifiedAt?: string | null
}

type ListEnvelope<T> = { data: T[]; meta?: { count?: number } }

export const guestsKey = (eventId: string) =>
  ["resource", "guests", { eventId }] as const
export const guestKey = (guestId: string) => ["guest", guestId] as const

// ── @mention roster ──────────────────────────────────────────────────────────
// The event roster the "@" composer typeahead reads. Sourced from the
// purpose-built `list-roster` chat action (forDm:false), which returns confirmed
// attendees that have an @handle — the action's own description calls this the
// "@mention typeahead" mode. (Staff/team members are not in this roster; the
// action is the designated mention source and we reuse it rather than inventing
// a new endpoint.) Cached per-event so a workspace switch refetches.
export type MentionRosterPerson = {
  userId: string
  handle: string | null
  displayName: string
  avatarImageId: string | null
  canDm: boolean
}

export const mentionRosterKey = (eventId: string) =>
  ["mention-roster", eventId] as const

export function mentionRosterQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: mentionRosterKey(eventId),
    queryFn: () =>
      api<{ success: true; roster: MentionRosterPerson[] }>(
        `/api/v1/event/${eventId}/chat/list-roster`,
        { method: "POST", body: { forDm: false } },
      ),
    select: (r) => r.roster,
    staleTime: 60_000,
  })
}

export function guestsQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: guestsKey(eventId),
    queryFn: () =>
      listAllData<Guest>("/api/v1/guests", { eventId, sort: "createdAt", order: "desc" }),
    select: (r) => r.data,
  })
}

// ── single guest record + its satellite data ─────────────────────────────────

export function guestQueryOptions(guestId: string) {
  return queryOptions({
    queryKey: guestKey(guestId),
    queryFn: () => api<Guest>(`/api/v1/guests/${guestId}`),
  })
}

// ── structured dietary (first-class, event-scoped) ───────────────────────────
// Shapes: docs/dietary-accessibility-model.md (mirrored in ./dietary).
export type DietaryAllergy = AllergyEntry
export type GuestDietary = {
  success: true
  guestId: string
  shared: boolean
  diets?: DietEntry[]
  // Derived kitchen signal: coeliac, trace-reactive or anaphylactic allergy.
  separatePrep?: boolean
  allergens?: DietaryAllergy[]
  notes?: string | null
  anaphylaxis?: boolean
  source?: string | null
  updatedAt?: string | null
}

export const guestDietaryKey = (guestId: string) => ["guest-dietary", guestId] as const

// Canonical allergen / diet taxonomy (EU-14 + FDA-9). Cached app-wide.
export type AllergenCatalogEntry = {
  value: string
  label: string
  group?: string
  regulatory: string[]
}
type TaxonOption = { value: string; label: string }
export type DietaryTaxonomy = {
  success: true
  taxonomy: {
    allergens: { kinds: string[]; catalog: AllergenCatalogEntry[]; severities: string[] }
    diets?: { kinds: TaxonOption[]; glutenFreeReasons: TaxonOption[]; diabetesTypes: TaxonOption[] }
  }
}

export function dietaryTaxonomyQueryOptions() {
  return queryOptions({
    queryKey: ["dietary-taxonomy"] as const,
    staleTime: 1000 * 60 * 60, // taxonomy rarely changes
    queryFn: () => api<DietaryTaxonomy>("/api/v1/me/preferences/taxonomy"),
  })
}

// Staff write of a guest's structured dietary (setGuestDietary). Replaces the
// whole record.
export function setGuestDietary(body: {
  guestId: string
  diets: DietEntry[] | null
  allergens: DietaryAllergy[] | null
  notes: string | null
}) {
  return api<{ success: true; guestId: string; updatedAt: string }>(
    "/api/v1/guests/set-dietary",
    { method: "POST", body },
  )
}

export function guestDietaryQueryOptions(guestId: string) {
  return queryOptions({
    queryKey: guestDietaryKey(guestId),
    queryFn: () =>
      api<GuestDietary>("/api/v1/guests/get-dietary", {
        method: "POST",
        body: { guestId },
      }),
  })
}

// ── structured accessibility (guest_accessibility, event-scoped) ─────────────
// No standalone read endpoint — it rides the GuestRecord bundle's guest slice.
export type GuestAccessibility =
  | { shared: true; needs: AccessibilityNeed[]; notes: string | null; updatedAt: string | null }
  | { shared: false }

export const guestAccessibilityKey = (guestId: string) => ["guest-accessibility", guestId] as const

export function guestAccessibilityQueryOptions(eventId: string, guestId: string) {
  return queryOptions({
    queryKey: guestAccessibilityKey(guestId),
    queryFn: () =>
      api<{ success: true; accessibility?: GuestAccessibility }>(
        `/api/v1/event/${eventId}/guests/${guestId}/bundle`,
        { method: "POST", body: { guestId, sections: ["guest"] } },
      ),
    select: (r): GuestAccessibility => r.accessibility ?? { shared: false },
  })
}

// ── event-wide dietary report (the whole-trip rollup) ────────────────────────
type SeverityCount = { total: number; anaphylaxisCount: number; crossContactCount: number }
export type DietaryReport = {
  success: true
  eventId: string
  total: number
  sharedCount: number
  notSharedCount: number
  plateHeadcount: number
  tagCounts: Record<string, number>
  // detail keys: coeliac/sensitivity/preference, certified/not_certified, type1/…
  dietCounts: { kind: string; total: number; detail: Record<string, number> }[]
  allergenCounts: ({ kind: string; group: string | null } & SeverityCount)[]
  allergenGroupCounts: { group: string; total: number; anaphylaxisCount: number }[]
  customAllergens: ({ label: string } & SeverityCount)[]
  anaphylaxisFlags: { guestId: string; displayName: string; kinds: string[]; notes: string | null }[]
  separatePrepCount: number
  separatePrepFlags: { guestId: string; displayName: string; reasons: string[] }[]
  notShared: { guestId: string; displayName: string; email: string | null }[]
  guests: {
    guestId: string
    displayName: string
    status?: string | null
    shared: boolean
    dietary: {
      diets: DietEntry[]
      separatePrep: boolean
      allergens: DietaryAllergy[]
      notes: string | null
    } | null
    source?: string | null
    updatedAt?: string | null
  }[]
}

export function eventDietaryReportQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: ["event-dietary-report", eventId] as const,
    queryFn: () =>
      api<DietaryReport>("/api/v1/guests/dietary-report", {
        method: "POST",
        body: { eventId },
      }),
  })
}

// ── event-wide accessibility (room-set) report ───────────────────────────────
export type AccessibilityReport = {
  success: true
  eventId: string
  total: number
  sharedCount: number
  notSharedCount: number
  withNeedsCount: number
  // detail keys: manual/power/scooter, asl/bsl/auslan/other, visual formats, relief_area
  needCounts: { kind: string; total: number; detail: Record<string, number> }[]
  // Only guests who told us something.
  guests: {
    guestId: string
    displayName: string
    needs: AccessibilityNeed[]
    notes: string | null
    updatedAt: string | null
  }[]
}

export function eventAccessibilityReportQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: ["event-accessibility-report", eventId] as const,
    queryFn: () =>
      api<AccessibilityReport>("/api/v1/guests/accessibility-report", {
        method: "POST",
        body: { eventId },
      }),
  })
}

// Render an allergen entry's human label (custom uses its label).
export function dietaryEntryLabel(e: { kind: string; label?: string | null }): string {
  if (e.kind === "other") return e.label || "Other"
  // Known kinds: prettify the snake_case value (taxonomy catalog has nicer
  // labels, but this is fine for chips without fetching the taxonomy).
  return e.kind.replace(/_/g, " ")
}

export type AgendaItem = {
  id: string
  name: string
  description?: string | null
  startsAt?: string | null
  endsAt?: string | null
  locationId?: string | null
  rsvpMode?: string | null
  groupId?: string | null
  // Present on the agenda-items list payload; optional here so the schedule
  // panel can show Required/Invite-only eyebrows + cancelled treatment.
  status?: string | null
  isRequired?: number | null
  accessMode?: string | null
  visibility?: string | null
}

export type AgendaRsvp = {
  id: string
  guestId: string
  agendaItemId: string
  status: string
  preApproved?: number | null
  createdAt?: string | null
  /** Reason the guest (or staff on their behalf) gave when opting out of a required item/group/series. */
  note?: string | null
}

export type CommsLog = {
  id: string
  guestId?: string | null
  channel: string
  category?: string | null
  subjectSnapshot?: string | null
  status: string
  sentAt?: string | null
  failedAt?: string | null
  triggeredByAction?: string | null
  createdAt?: string | null
}

export type FieldOption = { value: string; label: string }
export type AttendeeField = {
  id: string
  key: string
  label: string
  type: string
  options: FieldOption[] | null
  visibility: "admin_only" | "members_only" | "directory"
}
export type AttendeeGroup = {
  id: string
  name: string
  description: string | null
  visibility: "admin_only" | "members_only" | "directory"
  isAutomatic: boolean
  memberCount: number
}
export type AttendeeSetupResponse = {
  success: boolean
  fields?: AttendeeField[]
  groups?: AttendeeGroup[]
}
export type AttendeeFieldValue = {
  id: string
  fieldId: string
  fieldKey: string
  value: string | null
  source: string
  updatedAt?: string | null
}
export type AttendeeGroupMember = {
  id: string
  groupId: string
  guestId: string
  source: "rule" | "manual"
}

// A secure_text answer at rest: a pointer into the org's vault. The value is
// end-to-end encrypted — the server can't read it; staff reveal it through
// /seal/v1 with a grant + step-up.
export type SecureFieldRef = { __vaultRef: 1; vaultItemId: string }

export function isSecureFieldRef(v: unknown): v is SecureFieldRef {
  return (
    !!v &&
    typeof v === "object" &&
    !Array.isArray(v) &&
    (v as SecureFieldRef).__vaultRef === 1 &&
    typeof (v as SecureFieldRef).vaultItemId === "string"
  )
}

export type FieldSpec = {
  id: string
  type: string
  label: string
  options?: Array<{ value: string; label: string }>
  mode?: "date" | "datetime" | "time"
  maxRating?: number
  showIf?: { field: string; equals: string } | null
}
export type GuestForm = {
  id: string
  eventId: string
  audience: "event" | "agenda_item" | "agenda_group" | "individual"
  agendaItemId: string | null
  agendaGroupId: string | null
  title: string
  body: string | null
  createdAt: string
  completedAt: string | null
  fields: FieldSpec[] | null
  response: Record<string, unknown> | null
  responsePolicy: "editable" | "one_time" | null
  flavor: "generic" | "important"
  responseVisibility: "self" | "private" | null
  // Official / system-form marker (e.g. "emergency_contact"). Null = ordinary.
  systemKey?: string | null
}

export type SmsRecipientEligibility = {
  guestId: string
  hasConsent: boolean
  consentAt: string | null
  suppressed: boolean
  suppressedAt: string | null
}

export function guestRsvpsQueryOptions(guestId: string) {
  return queryOptions({
    queryKey: ["guest-rsvps", guestId] as const,
    queryFn: () =>
      listAllData<AgendaRsvp>("/api/v1/agenda-rsvps", { guestId }),
    select: (r) => r.data,
  })
}

export function agendaItemsQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: ["agenda-items", eventId] as const,
    queryFn: () =>
      listAllData<AgendaItem>("/api/v1/agenda-items", { eventId }),
    select: (r) => r.data,
  })
}

export function guestCommsQueryOptions(guestId: string) {
  return queryOptions({
    queryKey: ["guest-comms", guestId] as const,
    queryFn: () =>
      listAllData<CommsLog>("/api/v1/comms-log", { guestId }),
    select: (r) => r.data,
  })
}

export function guestFormsQueryOptions(eventId: string, guestId: string) {
  return queryOptions({
    queryKey: ["guest-forms", eventId, guestId] as const,
    queryFn: () =>
      api<{ forms: GuestForm[] }>(
        `/api/v1/event/${eventId}/guests/${guestId}/forms`,
        { method: "POST", body: { guestId } },
      ),
    select: (r) => r.forms,
  })
}

// STAFF set/edit a guest's response to a form-todo (emergency contact,
// accessibility, …). Backed by the admin/member `set-guest-response` action;
// the server cleans to visible known fields + upserts the assignment row.
export async function setGuestResponse(input: {
  eventId: string
  todoId: string
  guestId: string
  response: Record<string, unknown>
  completed?: boolean
}): Promise<{ success: boolean; completedAt: string | null }> {
  return api(`/api/v1/event/${input.eventId}/todos/${input.todoId}/set-guest-response`, {
    method: "POST",
    body: {
      todoId: input.todoId,
      guestId: input.guestId,
      response: input.response,
      completed: input.completed ?? true,
    },
  })
}

export function attendeeSetupQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: ["attendee-fields-setup", eventId, "guest-overview"] as const,
    queryFn: () =>
      api<AttendeeSetupResponse>(`/api/v1/event/${eventId}/attendee-fields/setup`, {
        method: "POST",
        body: {},
      }),
  })
}

export function attendeeFieldValuesQueryOptions(eventId: string, guestId: string) {
  return queryOptions({
    queryKey: ["guest-attendee-field-values", eventId, guestId] as const,
    queryFn: () =>
      listAllData<AttendeeFieldValue>("/api/v1/attendee-field-values", { eventId, guestId }),
    select: (r) => r.data,
  })
}

// Set (or clear) a guest's attendee custom-field value. The action re-evaluates
// the guest's automatic-group memberships, so callers should invalidate BOTH
// the field-values and group-members queries on success.
export function setAttendeeFieldValue(
  eventId: string,
  input: { guestId: string; fieldId: string; value?: unknown; clear?: boolean },
) {
  return api<{ committed: boolean; guestId: string; fieldId: string }>(
    `/api/v1/event/${eventId}/attendee-fields/set-value`,
    { method: "POST", body: input },
  )
}

// ── bulk edit (spreadsheet-style) ────────────────────────────────────────────
// Both batch actions are dryRun-first: the dry-run plan (old → new per cell)
// is the BulkEditSheet's review screen; commit re-sends the same payload.

export type BatchGuestFields = {
  firstName?: string
  lastName?: string | null
  badgeFirstName?: string | null
  badgeLastName?: string | null
  titleAtInvite?: string | null
  companyAtInvite?: string | null
  phoneAtInvite?: string | null
  displayName?: string | null
  dietaryNotes?: string | null
  specialRequests?: string | null
  personalMessage?: string | null
  preApproved?: boolean
  maxGuests?: number
}
export type BatchGuestPlan = {
  guestId: string
  action: "update" | "skip"
  reason?: string
  name?: string | null
  changes?: Record<string, { from: unknown; to: unknown }>
}
export type BatchGuestsResult = {
  success: boolean
  committed: boolean
  plans: BatchGuestPlan[]
  summary: { willUpdate: number; willSkip: number }
}
export function batchUpdateGuests(input: {
  eventId: string
  updates: Array<{ guestId: string; fields: BatchGuestFields }>
  dryRun: boolean
}) {
  return api<BatchGuestsResult>("/api/v1/guests/batch-update", {
    method: "POST",
    body: input,
  })
}

export type BatchFieldValuePlan = {
  guestId: string
  fieldId: string
  fieldKey?: string
  action: "set" | "clear" | "skip"
  reason?: string
  from?: unknown
  to?: unknown
}
export type BatchFieldValuesResult = {
  success: boolean
  committed: boolean
  plans: BatchFieldValuePlan[]
  summary: { willSet: number; willClear: number; willSkip: number }
}
export function batchSetAttendeeFieldValues(
  eventId: string,
  input: {
    updates: Array<{ guestId: string; fieldId: string; value?: unknown; clear?: boolean }>
    dryRun: boolean
  },
) {
  return api<BatchFieldValuesResult>(
    `/api/v1/event/${eventId}/attendee-fields/batch-set-values`,
    { method: "POST", body: input },
  )
}

// Event-wide custom-field values (one row per guest × field that has a value)
// for the bulk-edit grid. The per-guest variant above stays for GuestRecord.
export function eventAttendeeFieldValuesQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: ["event-attendee-field-values", eventId] as const,
    queryFn: () =>
      listAllData<AttendeeFieldValue & { guestId: string }>("/api/v1/attendee-field-values", {
        eventId,
      }),
  })
}

export function attendeeGroupMembersQueryOptions(eventId: string, guestId: string) {
  return queryOptions({
    queryKey: ["guest-attendee-group-members", eventId, guestId] as const,
    queryFn: () =>
      listAllData<AttendeeGroupMember>("/api/v1/attendee-group-members", { eventId, guestId }),
    select: (r) => r.data,
  })
}

export function smsEligibilityQueryOptions(eventId: string, guestId: string) {
  return queryOptions({
    queryKey: ["sms-eligibility", eventId, guestId] as const,
    queryFn: () =>
      api<{ recipients: SmsRecipientEligibility[] }>("/api/v1/comms/sms-eligibility", {
        method: "POST",
        body: { eventId, guestIds: [guestId] },
      }),
    select: (r) => r.recipients.find((rec) => rec.guestId === guestId) ?? null,
  })
}

// Plus-ones attached to a host guest.
export type PlusOneRow = {
  id: string
  firstName?: string | null
  lastName?: string | null
  displayName?: string | null
  nameAtInvite?: string | null
  emailAtInvite: string
  status: string
  relationshipToHost?: string | null
  sentAt?: string | null
}

export function plusOnesQueryOptions(eventId: string, hostGuestId: string) {
  return queryOptions({
    queryKey: ["guest-plus-ones", hostGuestId] as const,
    queryFn: () =>
      listAllData<PlusOneRow>("/api/v1/guests", { eventId, hostGuestId }),
    select: (r) => r.data,
  })
}

// ── guest record actions ─────────────────────────────────────────────────────

/** POST a record-scoped guest action (send-invite, check-in, cancel-invite…). */
export function guestAction(guestId: string, action: string, body: unknown = {}) {
  return api<Record<string, unknown>>(`/api/v1/guests/${guestId}/${action}`, {
    method: "POST",
    body,
  })
}

/** Dry-run payload for setTicketType — what the confirm step shows. Note the
 *  three `*Unchanged` flags: the override never touches money, the order, or
 *  inventory, and the UI says so out loud when a paid order exists. */
export type TicketTypeChangePreview = {
  guestId: string
  guestName: string | null
  from: string | null
  to: string | null
  paidOrder: {
    id: string
    purchasedTierId: string
    amountCents: number
    currency: string
  } | null
  moneyUnchanged: boolean
  orderUnchanged: boolean
  inventoryUnchanged: boolean
  willRecalcGroups: boolean
}

/** Staff override of a guest's tier. `ticketTypeId: null` clears it. Always
 *  dry-run first — the preview is what tells the operator the paid order and
 *  the charge stay exactly as they were. */
export function setGuestTicketType(
  guestId: string,
  ticketTypeId: string | null,
  dryRun = false,
) {
  return guestAction(guestId, "set-ticket-type", { ticketTypeId, dryRun }) as Promise<{
    success: true
    dryRun?: boolean
    preview?: TicketTypeChangePreview
    ticketTypeId?: string | null
    ticketTypeName?: string | null
  }>
}

/** What `issueHandoutCode` returns — a revocable, expiring organizer-handout
 *  invite code minted WITHOUT emailing, for a printed QR / Slack handoff. It is
 *  an Event Pass redeemed sessionless at enterEvent — never a BA account —
 *  and `inviteUrl` is the string the apps' QR scanners decode.
 *
 *  ⚠ BEARER LOGIN CREDENTIAL: anyone holding `inviteUrl`/`code` can enter the
 *  event as this guest. Only render it on artifacts handed privately to the
 *  guest (invite-code cards, DMs) — NEVER on publicly-worn/displayed ones
 *  (name badges use the staff-only guest-record link instead; BadgeSheet). */
export type HandoutCode = {
  success?: boolean
  code: string
  codeDisplay: string
  inviteUrl: string
  email: string
  expiresAt: string
  issuedAt?: string
}

/** Get this guest's live handout code, minting one only if none exists
 *  (revocable, expiring; default 30-day TTL, server-capped at 120). Does NOT
 *  email — returns the code + QR url to show. Pass forceNew to rotate to a
 *  fresh code. */
export function issueHandoutCode(
  guestId: string,
  opts: { ttlDays?: number; forceNew?: boolean } = {},
) {
  return api<HandoutCode>(`/api/v1/guests/${guestId}/issue-handout-code`, {
    method: "POST",
    body: {
      ...(opts.ttlDays != null ? { ttlDays: opts.ttlDays } : {}),
      ...(opts.forceNew ? { forceNew: true } : {}),
    },
  })
}

/** Stamp a guest's badge as printed — the console asks for confirmation after
 *  the print dialog since the browser can't confirm paper actually came out.
 *  Reprints re-stamp; `clear` wipes the print record. The activity feed keeps
 *  the history either way. */
export function markBadgePrinted(guestId: string, opts: { clear?: boolean } = {}) {
  return guestAction(guestId, "mark-badge-printed", opts.clear ? { clear: true } : {})
}

// ── saved badge settings ─────────────────────────────────────────────────────
// One row per event holding the BadgeSheet options JSON (design, paper, sizes,
// colors, logo…) so every staffer prints the same badges. Spec is validated
// client-side on load, mirroring saved_reports.

export type BadgeSettingsRecord = { id: string; eventId: string; spec: string }

export const badgeSettingsKey = (eventId: string) => ["badge-settings", eventId] as const

export function badgeSettingsQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: badgeSettingsKey(eventId),
    queryFn: () =>
      api<ListEnvelope<BadgeSettingsRecord>>("/api/v1/badge-settings", {
        searchParams: { eventId, limit: 1 },
      }),
    select: (r) => r.data[0] ?? null,
  })
}

export function saveBadgeSettings(eventId: string, spec: string, existingId?: string) {
  return existingId
    ? api<Record<string, unknown>>(`/api/v1/badge-settings/${existingId}`, {
        method: "PATCH",
        body: { spec },
      })
    : api<Record<string, unknown>>("/api/v1/badge-settings", {
        method: "POST",
        body: { eventId, spec },
      })
}

/** Revoke all of this guest's live handout codes (the printed placard is dead). */
export function revokeHandoutCodes(guestId: string) {
  return api<{ success?: boolean; revoked?: number }>(
    `/api/v1/guests/${guestId}/revoke-handout-codes`,
    { method: "POST", body: {} },
  )
}

export function patchGuest(guestId: string, patch: Partial<Guest>) {
  return api<Record<string, unknown>>(`/api/v1/guests/${guestId}`, {
    method: "PATCH",
    body: patch,
  })
}

export function deleteGuest(guestId: string) {
  return api<null>(`/api/v1/guests/${guestId}`, { method: "DELETE" })
}

// ── lifecycle action registry + status gating ────────────────────────────────
// Single source of truth so the People list rows + bulk bar + the GuestRecord
// header/footer all agree on which lifecycle actions a guest's status permits,
// and on the endpoint + success copy + optimistic next-status for each. The
// record endpoints mirror the backend guests/actions transitions:
//   send-invite  draft|invited|confirmed|waitlisted → invited (resend; status-preserving)
//   approve-invite pending           → confirmed + invite email (POST approve {sendInvite})
//   confirm      draft|invited|pending|declined → confirmed (offline RSVP)
//   approve      pending|waitlisted  → confirmed (waitlist seat)
//   check-in     confirmed           → confirmed (+ checkedInAt)
//   check-out    checked-in          → confirmed (+ checkedOutAt)
//   cancel-invite (most non-terminal) → rescinded (destructive)
// `confirm`/`approve` stamp respondedAt; the optimistic patch only flips status
// (+ checkedInAt for check-in) — the cache invalidate refetches the rest.

export type GuestLifecycleId =
  | "send-invite"
  | "confirm"
  | "approve"
  | "approve-invite"
  | "check-in"
  | "check-out"
  | "cancel-check-in"
  | "cancel-check-out"
  | "cancel-invite"
  | "send-calendar-invite"
  | "cancel-calendar-invite"

/** Statuses the backend sendInvite transition accepts (draft first-send, plus
 *  the status-preserving resends). Pending is deliberately absent. */
export const INVITE_SENDABLE_STATUSES = ["draft", "invited", "confirmed", "waitlisted"]

export type GuestLifecycleSpec = {
  id: GuestLifecycleId
  /** Record endpoint to POST when it differs from `id` (e.g. approve-invite → approve). */
  endpoint?: string
  /** Fixed request body merged under any caller-supplied body. */
  body?: Record<string, unknown>
  /** Imperative button/menu label, status-aware (draft vs resend). */
  label: (g: Pick<Guest, "status">) => string
  /** Toast on success. */
  done: (g: Pick<Guest, "status">) => string
  /** Whether the action applies to a guest in this state. */
  enabled: (
    g: Pick<
      Guest,
      "status" | "checkedInAt" | "checkedOutAt" | "hostGuestId" | "sentAt" | "calendarInviteSentAt"
    >,
  ) => boolean
  /** Destructive → callers should confirm before running. */
  danger?: boolean
  /** Optimistic guest patch applied before the request resolves. */
  optimistic?: (g: Guest) => Partial<Guest>
}

export const GUEST_LIFECYCLE: Record<GuestLifecycleId, GuestLifecycleSpec> = {
  "send-invite": {
    id: "send-invite",
    label: (g) => (g.status === "draft" ? "Send invite" : "Resend invite"),
    done: (g) => (g.status === "draft" ? "Invite sent" : "Invite resent"),
    // Backend transition allows draft→invited (first send), the invited→invited
    // resend, and a status-PRESERVING resend to confirmed/waitlisted guests
    // (they lost the email — re-deliver the credential without resetting RSVP).
    // NOT pending: the backend has no pending→invited transition (it 409s) —
    // a pending guest is awaiting a seating decision, so the console offers
    // "Approve & send invite" (approve-invite) instead.
    enabled: (g) => INVITE_SENDABLE_STATUSES.includes(g.status),
    // Only ADVANCE to invited from a not-yet-responded state; a resend to a
    // confirmed/waitlisted guest keeps their status (matches the backend).
    optimistic: (g) => (["confirmed", "waitlisted"].includes(g.status) ? {} : { status: "invited" }),
  },
  confirm: {
    id: "confirm",
    label: () => "Confirm",
    done: () => "Confirmed",
    // Offline RSVP path. Not for already-confirmed / checked-in / terminal.
    enabled: (g) => ["draft", "invited", "pending", "declined"].includes(g.status),
    optimistic: () => ({ status: "confirmed", preApproved: 1 }),
  },
  approve: {
    id: "approve",
    label: () => "Approve",
    done: () => "Approved",
    // Waitlist seat grant. A plus-one REQUEST (host-added, not yet invited)
    // must go through approve-plus-one instead — exclude it here.
    enabled: (g) =>
      ["pending", "waitlisted"].includes(g.status) && !(g.hostGuestId && !g.sentAt),
    optimistic: () => ({ status: "confirmed" }),
  },
  // The pending-guest counterpart of "Send invite": confirm the seat AND mint
  // + email the credential in one call (guests/approve with sendInvite:true —
  // fails before mutating if the event isn't published). Plus-one requests are
  // excluded the same way as `approve`.
  "approve-invite": {
    id: "approve-invite",
    endpoint: "approve",
    body: { sendInvite: true },
    label: () => "Approve & send invite",
    done: () => "Approved & invited",
    enabled: (g) => g.status === "pending" && !(g.hostGuestId && !g.sentAt),
    optimistic: () => ({ status: "confirmed" }),
  },
  "check-in": {
    id: "check-in",
    label: () => "Check in",
    done: () => "Checked in",
    enabled: (g) => g.status === "confirmed" && !g.checkedInAt,
    optimistic: () => ({ checkedInAt: new Date().toISOString() }),
  },
  "check-out": {
    id: "check-out",
    label: () => "Check out",
    done: () => "Checked out",
    // Checked in (timestamp set) but not yet checked out. Status stays
    // "confirmed" through the whole check-in/out cycle.
    enabled: (g) => !!g.checkedInAt && !g.checkedOutAt,
    optimistic: () => ({ checkedOutAt: new Date().toISOString() }),
  },
  // Corrections — undo an accidental check-in/out. Clearing the check-in clears
  // the whole cycle; clearing the check-out reverts to checked-in.
  "cancel-check-in": {
    id: "cancel-check-in",
    label: () => "Clear check-in",
    done: () => "Check-in cleared",
    danger: true,
    enabled: (g) => !!g.checkedInAt,
    optimistic: () => ({ checkedInAt: null, checkedOutAt: null }),
  },
  "cancel-check-out": {
    id: "cancel-check-out",
    label: () => "Clear check-out",
    done: () => "Check-out cleared",
    danger: true,
    enabled: (g) => !!g.checkedOutAt,
    optimistic: () => ({ checkedOutAt: null }),
  },
  "cancel-invite": {
    id: "cancel-invite",
    label: () => "Cancel invite",
    done: () => "Invite cancelled",
    danger: true,
    enabled: (g) => !["rescinded", "cancelled", "declined"].includes(g.status),
    optimistic: () => ({ status: "rescinded" }),
  },
  // Calendar (ICS) invites — explicit, opt-in, and separate from the RSVP
  // invite. Each send goes out individually (one email/ATTENDEE per guest).
  "send-calendar-invite": {
    id: "send-calendar-invite",
    label: () => "Send calendar invite",
    done: () => "Calendar invite sent",
    // Offer for guests who are actually invited/attending. The backend also
    // requires an email; states it can't serve 409 with a surfaced toast.
    enabled: (g) => ["invited", "confirmed", "waitlisted"].includes(g.status),
    optimistic: () => ({ calendarInviteSentAt: new Date().toISOString() }),
  },
  "cancel-calendar-invite": {
    id: "cancel-calendar-invite",
    label: () => "Cancel calendar invite",
    done: () => "Calendar invite cancelled",
    danger: true,
    // Only meaningful once a calendar invite is actually outstanding.
    enabled: (g) => !!g.calendarInviteSentAt,
    optimistic: () => ({ calendarInviteSentAt: null }),
  },
}

/** The lifecycle actions a guest in this state can take, in menu order. */
export function guestLifecycleActions(
  g: Pick<
    Guest,
    "status" | "checkedInAt" | "checkedOutAt" | "hostGuestId" | "sentAt" | "calendarInviteSentAt"
  >,
): GuestLifecycleSpec[] {
  const order: GuestLifecycleId[] = [
    "send-invite",
    "approve-invite",
    "approve",
    "confirm",
    "check-in",
    "check-out",
    "send-calendar-invite",
    "cancel-calendar-invite",
    "cancel-check-in",
    "cancel-check-out",
    "cancel-invite",
  ]
  return order.map((id) => GUEST_LIFECYCLE[id]).filter((s) => s.enabled(g))
}

// ── optimistic cache patching ────────────────────────────────────────────────
// Patch BOTH the event guests list (["resource","guests",{eventId}]) and the
// single guest record (["guest", id]) so the row + the open record update
// instantly; the realtime layer + a follow-up invalidate reconcile to truth.

export function patchGuestCaches(qc: QueryClient, guestId: string, patch: Partial<Guest>) {
  const apply = (rows: Guest[]) =>
    rows.map((r) => (r.id === guestId ? { ...r, ...patch } : r))
  // The list query caches the raw LIST ENVELOPE ({ data: Guest[] }) — its
  // `select` only unwraps it for components — so patch `.data`, not the value
  // directly. Tolerate a bare array too (other guests-prefixed caches).
  qc.setQueriesData({ queryKey: ["resource", "guests"] }, (data: unknown) => {
    if (Array.isArray(data)) return apply(data as Guest[])
    if (data && typeof data === "object" && Array.isArray((data as { data?: unknown }).data)) {
      const env = data as { data: Guest[] }
      return { ...env, data: apply(env.data) }
    }
    return data
  })
  qc.setQueryData<Guest | undefined>(guestKey(guestId), (g) =>
    g ? { ...g, ...patch } : g,
  )
}

export function invalidateGuestCaches(qc: QueryClient, eventId?: string | null, guestId?: string) {
  if (guestId) void qc.invalidateQueries({ queryKey: guestKey(guestId) })
  if (eventId) void qc.invalidateQueries({ queryKey: guestsKey(eventId) })
  void qc.invalidateQueries({ queryKey: ["resource", "guests"] })
}

/**
 * Run a single lifecycle action against a guest with an optimistic cache patch.
 * Rolls the patch back on failure (re-reading the snapshot), then invalidates so
 * the caches settle on server truth. Returns the action's JSON result.
 */
export async function runGuestLifecycle(
  qc: QueryClient,
  guest: Guest,
  spec: GuestLifecycleSpec,
  body: unknown = {},
): Promise<Record<string, unknown>> {
  const patch = spec.optimistic?.(guest)
  if (patch) patchGuestCaches(qc, guest.id, patch)
  try {
    const res = await guestAction(guest.id, spec.endpoint ?? spec.id, {
      ...(spec.body ?? {}),
      ...((body as Record<string, unknown> | null) ?? {}),
    })
    invalidateGuestCaches(qc, guest.eventId, guest.id)
    return res
  } catch (err) {
    // Roll back the optimistic fields to their pre-action values.
    if (patch) {
      const revert: Partial<Guest> = {}
      for (const k of Object.keys(patch) as (keyof Guest)[]) {
        ;(revert as Record<string, unknown>)[k] = guest[k]
      }
      patchGuestCaches(qc, guest.id, revert)
    }
    throw err
  }
}

export function actionErrorMessage(err: unknown, fallback = "Action failed"): string {
  if (err instanceof ApiError) return err.message || fallback
  if (err instanceof Error) return err.message || fallback
  return fallback
}

/**
 * Iterate a lifecycle action across many guests (per-guest granularity so one
 * 409 doesn't sink the batch), patching each optimistically. Returns ok/failed
 * counts plus the first error message for the toast.
 */
export async function runGuestLifecycleBulk(
  qc: QueryClient,
  guests: Guest[],
  spec: GuestLifecycleSpec,
  body: unknown = {},
): Promise<{ ok: number; failed: number; firstError?: string }> {
  const targets = guests.filter((g) => g.id && spec.enabled(g))
  if (targets.length === 0) return { ok: 0, failed: 0 }
  const results = await Promise.allSettled(
    targets.map((g) => runGuestLifecycle(qc, g, spec, body)),
  )
  let ok = 0
  let firstError: string | undefined
  for (const r of results) {
    if (r.status === "fulfilled") ok++
    else if (!firstError) firstError = actionErrorMessage(r.reason)
  }
  return { ok, failed: results.length - ok, firstError }
}

/**
 * Delete guests permanently. The backend only permits deleting RESCINDED
 * guests (cancel the invite first) — per-guest requests so one 403 doesn't
 * sink the batch, mirroring runGuestLifecycleBulk.
 */
export async function deleteGuestsBulk(
  qc: QueryClient,
  targets: Guest[],
): Promise<{ ok: number; failed: number; firstError?: string }> {
  if (targets.length === 0) return { ok: 0, failed: 0 }
  const results = await Promise.allSettled(targets.map((g) => deleteGuest(g.id)))
  let ok = 0
  let firstError: string | undefined
  for (const r of results) {
    if (r.status === "fulfilled") ok++
    else if (!firstError) firstError = actionErrorMessage(r.reason, "Delete failed")
  }
  invalidateGuestCaches(qc, targets[0]?.eventId)
  return { ok, failed: results.length - ok, firstError }
}

// One ticket per guest via the same endpoint the admin ComposeTicketDialog hits.
export type SendTicketResult = {
  success: boolean
  recipientCount?: number
  tickets?: Array<{ ticketId: string; guestUserId: string }>
  skippedUserIds?: string[]
  error?: string
}
// Keyed on GUEST ids so guests without an account (Event Pass only) can be
// messaged — the ticket reaches them on their pass.
export function sendGuestTicket(input: {
  eventId: string
  subject: string
  body: string
  guestIds: string[]
}) {
  return api<SendTicketResult>("/api/v1/chat/send-to-support-tickets", {
    method: "POST",
    body: {
      eventId: input.eventId,
      subject: input.subject.trim(),
      body: input.body.trim(),
      recipientRule: { kind: "guest_ids", guestIds: input.guestIds },
      dryRun: false,
    },
  })
}

// New-guest creation (mirrors admin AddGuestDialog → POST /api/v1/guests).
// NOTE: `sendInvite` is NOT a guests column — the create route rejects unknown
// fields (400 fieldNotCreateable). Sending the invite is a SECOND call to the
// send-invite action; see `createGuestAndMaybeInvite`.
export type CreateGuestInput = {
  eventId: string
  firstName: string
  lastName?: string
  email: string
  title?: string
  company?: string
}
export function createGuest(input: CreateGuestInput) {
  return api<{ id?: string } & Record<string, unknown>>("/api/v1/guests", {
    method: "POST",
    body: {
      eventId: input.eventId,
      firstName: input.firstName.trim(),
      lastName: input.lastName?.trim() || undefined,
      nameAtInvite: joinName(input.firstName, input.lastName),
      emailAtInvite: input.email.trim(),
      titleAtInvite: input.title?.trim() || undefined,
      companyAtInvite: input.company?.trim() || undefined,
    },
  })
}

// ── spreadsheet import (guests__importGuests) ────────────────────────────────
// The server matches every row case-insensitively on email against the
// event's guest list and the rest of the file. Always dry-run first; the
// commit returns the same per-row shape plus the new guest ids.

export type GuestImportRowInput = {
  rowIndex: number
  firstName: string
  lastName?: string
  email: string
  title?: string
  company?: string
}

export type GuestImportOutcome = "create" | "existing" | "duplicate-in-file" | "invalid"

export type GuestImportRowResult = {
  rowIndex: number
  outcome: GuestImportOutcome
  action: "create" | "update" | "skip"
  email: string
  name: string
  guestId: string | null
  existingStatus: string | null
  existingName: string | null
  errors: string[]
}

export type GuestImportResult = {
  success: boolean
  dryRun: boolean
  summary: {
    rows: number
    create: number
    update: number
    alreadyOnList: number
    duplicateInFile: number
    invalid: number
  }
  rows: GuestImportRowResult[]
}

/** Max rows per request (the action's bound); callers chunk larger files. */
export const GUEST_IMPORT_CHUNK = 500

export async function importGuests(input: {
  eventId: string
  rows: GuestImportRowInput[]
  onExisting: "skip" | "update"
  dryRun: boolean
}): Promise<GuestImportResult> {
  // Chunk on the client; the server checks each chunk against the guest list,
  // so in-file duplicates across chunks are caught as "existing" on commit
  // (the earlier chunk has already been inserted) and by the client-side
  // in-file check during the dry run.
  const merged: GuestImportResult = {
    success: true,
    dryRun: input.dryRun,
    summary: { rows: 0, create: 0, update: 0, alreadyOnList: 0, duplicateInFile: 0, invalid: 0 },
    rows: [],
  }
  for (let i = 0; i < input.rows.length; i += GUEST_IMPORT_CHUNK) {
    const res = await api<GuestImportResult>("/api/v1/guests/import", {
      method: "POST",
      body: {
        eventId: input.eventId,
        rows: input.rows.slice(i, i + GUEST_IMPORT_CHUNK),
        onExisting: input.onExisting,
        dryRun: input.dryRun,
      },
    })
    for (const k of Object.keys(merged.summary) as (keyof GuestImportResult["summary"])[]) {
      merged.summary[k] += res.summary[k] ?? 0
    }
    merged.rows.push(...res.rows)
  }
  return merged
}

// Create a guest, then — when requested — fire the send-invite action against
// the freshly-created (draft) record so the invite email actually goes out.
// Returns the new guest id (when the API echoes it). Throws if the invite send
// fails AFTER a successful create, so the caller can surface "added, but invite
// failed" rather than silently claiming success.
export async function createGuestAndMaybeInvite(
  input: CreateGuestInput & { sendInvite?: boolean },
): Promise<{ id?: string; invited: boolean }> {
  const res = await createGuest(input)
  const id = typeof res?.id === "string" ? res.id : undefined
  if (input.sendInvite && id) {
    await guestAction(id, "send-invite")
    return { id, invited: true }
  }
  return { id, invited: false }
}

// ── org on-file details ──────────────────────────────────────────────────────

/** One person's carry-over details as spreadsheet text — the server parses
 *  diets / allergies / accessibility against the dietary taxonomy. */
export type OnFileImportRow = {
  rowIndex: number
  email: string
  name?: string
  dietsText?: string
  allergiesText?: string
  dietaryNotes?: string
  shirtSize?: string
  accessibilityText?: string
}

export type OnFileImportResult = {
  summary: { total: number; saved: number; createdContacts: number; skipped: number; withWarnings: number }
  rows: Array<{
    rowIndex: number
    status: "saved" | "skipped"
    reason?: string
    warnings: string[]
  }>
}

/** Save what the org has on file for people (e.g. last event's spreadsheet)
 *  onto their org contacts. It prefills their forms at every future event —
 *  the guest still confirms by submitting. Matches contacts by email and
 *  creates any that are missing. */
export function saveContactsOnFile(input: {
  organizationId: string
  rows: OnFileImportRow[]
  sourceLabel?: string
  dryRun?: boolean
}) {
  return api<OnFileImportResult>("/api/v1/contacts/on-file", {
    method: "POST",
    body: { ...input, dryRun: input.dryRun ?? false },
  })
}

// ── display helpers ──────────────────────────────────────────────────────────

/** Badge / display name, falling back through legal name → invite name. */
export function guestDisplayName(g: Guest): string {
  // Badge names are per-PART overrides: someone who set "Dee" as the badge
  // first name still has a surname, and dropping it printed a mononym.
  const badgeFirst = g.badgeFirstName?.trim()
  const badgeLast = g.badgeLastName?.trim()
  const badge =
    badgeFirst || badgeLast
      ? joinName(badgeFirst || g.firstName, badgeLast || g.lastName)
      : ""
  if (badge) return badge
  if (g.displayName) return g.displayName
  const legal = joinName(g.firstName, g.lastName)
  return legal || g.nameAtInvite || g.emailAtInvite || "Guest"
}

export function joinName(
  first?: string | null,
  last?: string | null,
): string {
  return [first, last].map((p) => (p ?? "").trim()).filter(Boolean).join(" ")
}

/** Buckets the many backend statuses into the four RSVP saved-view lanes. */
export type RsvpBucket = "confirmed" | "pending" | "declined" | "other"

export function rsvpBucket(status: string): RsvpBucket {
  switch (status) {
    case "confirmed":
    case "checked-in":
    case "checked-out":
      return "confirmed"
    case "draft":
    case "invited":
    case "pending":
    case "waitlisted":
      return "pending"
    case "declined":
    case "cancelled":
      return "declined"
    default:
      return "other"
  }
}

export function statusLabel(status: string): string {
  return status ? status.replace(/[-_]/g, " ").replace(/^./, (c) => c.toUpperCase()) : "—"
}

/** The guest TABLE's status-pill palette, keyed by displayStatus(). Shared by
 *  the People and Badges lists so the pills match everywhere. */
export const STATUS_BADGE_CLS: Record<string, string> = {
  confirmed:
    "text-emerald-700 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-500/15",
  "checked-in":
    "text-emerald-700 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-500/15",
  "checked-out":
    "text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-white/10",
  pending: "text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-500/15",
  waitlisted: "text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-500/15",
  invited: "text-sky-700 dark:text-sky-400 bg-sky-100 dark:bg-sky-500/15",
  draft: "text-plum-700/70 dark:text-plum-200/55 bg-plum-100 dark:bg-white/10",
  declined: "text-rose-600 dark:text-rose-400 bg-rose-100 dark:bg-rose-500/15",
  cancelled: "text-plum-600/60 dark:text-plum-300/45 bg-plum-50 dark:bg-white/5",
}

/** The status to SHOW. Check-in/out are tracked on checkedInAt / checkedOutAt
 *  while the stored `status` stays "confirmed", so a checked-in guest must read
 *  "Checked in" (and a checked-out one "Checked out"), not "Confirmed". */
export function displayStatus(
  g: Pick<Guest, "status" | "checkedInAt" | "checkedOutAt">,
): string {
  if (g.checkedOutAt) return "checked-out"
  if (g.checkedInAt) return "checked-in"
  return g.status
}

/** First name for friendly copy ("Open chat with Ada"). */
export function firstNameOf(name: string | null | undefined): string {
  if (!name) return "guest"
  return name.trim().split(/\s+/)[0] || "guest"
}

export function plusOneName(r: {
  displayName?: string | null
  firstName?: string | null
  lastName?: string | null
  nameAtInvite?: string | null
  emailAtInvite?: string
}): string {
  return (
    r.displayName?.trim() ||
    joinName(r.firstName, r.lastName) ||
    r.nameAtInvite?.trim() ||
    r.emailAtInvite ||
    "Guest"
  )
}

// ── form-response value rendering (ported from admin lib/form-response) ───────

// One entry in an `allergies`-field answer, mirroring guest_dietary.allergens.
export type AllergenAnswer = {
  kind?: string
  label?: string | null
  anaphylaxis?: boolean
  crossContact?: boolean
}

// Best-effort humanization of an allergen `kind` slug without the server
// taxonomy in hand (e.g. "tree_nut" → "Tree nut"). Catalog labels are richer
// where the taxonomy is loaded (AllergiesFieldInput); this is the fallback for
// table cells / CSV.
export function humanizeAllergenKind(kind: string): string {
  if (!kind) return ""
  return kind
    .split("_")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ")
}

export function isResponseField(field: FieldSpec): boolean {
  return !["section_heading", "paragraph", "divider"].includes(field.type)
}

export function formatResponseCell(field: FieldSpec, raw: unknown): string {
  if (raw == null) return "—"
  switch (field.type) {
    case "short_text":
    case "long_text":
    case "date":
      return typeof raw === "string" ? raw : "—"
    case "number":
      return typeof raw === "number" ? String(raw) : "—"
    case "boolean":
      return raw === true ? "Yes" : raw === false ? "No" : "—"
    case "rating":
      return typeof raw === "number" ? `${raw} / ${field.maxRating ?? 5}` : "—"
    case "single_select": {
      if (typeof raw !== "string") return "—"
      return (field.options ?? []).find((o) => o.value === raw)?.label ?? raw
    }
    case "multi_select": {
      if (!Array.isArray(raw)) return "—"
      const byValue = new Map((field.options ?? []).map((o) => [o.value, o.label] as const))
      return (raw as string[]).map((v) => byValue.get(v) ?? v).join(", ")
    }
    case "signature":
      return parseSignature(raw) ? "Signed" : "—"
    case "attachment": {
      const files = parseAttachments(raw)
      return files.length ? files.map((f) => f.filename).join(", ") : "—"
    }
    case "allergies": {
      if (!Array.isArray(raw)) return "—"
      const parts = (raw as AllergenAnswer[])
        .map((a) => {
          const name =
            a?.kind === "other"
              ? a.label?.trim() || "Other"
              : humanizeAllergenKind(a?.kind ?? "")
          if (!name) return ""
          return a?.anaphylaxis ? `${name} (severe)` : a?.crossContact ? `${name} (trace)` : name
        })
        .filter(Boolean)
      return parts.length ? parts.join(", ") : "—"
    }
    // End-to-end encrypted: the response only ever holds a vault reference,
    // never the value. Revealing it is a separate, step-up-gated action.
    case "secure_text":
      return isSecureFieldRef(raw) ? "••••" : "—"
    case "section_heading":
    case "paragraph":
    case "divider":
      return "—"
    default:
      return typeof raw === "string" ? raw : JSON.stringify(raw)
  }
}

/** Format a stored custom attendee-field value (JSON-encoded) for display. */
export function formatAttendeeFieldValue(field: AttendeeField, raw: string | null): string {
  if (raw == null || raw === "") return "—"
  let value: unknown = raw
  try {
    value = JSON.parse(raw)
  } catch {
    /* keep raw string */
  }
  if (Array.isArray(value) && value.length === 0) return "—"
  return formatResponseCell(
    { id: field.id, type: field.type, label: field.label, options: field.options ?? undefined },
    value,
  )
}

// ── signature + attachment parsing (ported from admin lib) ───────────────────

export type SignaturePoint = [number, number]
export type SignaturePayload = { v: 1; strokes: SignaturePoint[][] }

const MAX_SIG_LEN = 100_000
const MAX_SIG_STROKES = 64
const MAX_SIG_POINTS = 8_192

export function parseSignature(raw: unknown): SignaturePayload | null {
  if (typeof raw !== "string" || raw.length === 0 || raw.length > MAX_SIG_LEN) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  if (!parsed || typeof parsed !== "object") return null
  const payload = parsed as { v?: unknown; strokes?: unknown }
  if (payload.v !== 1 || !Array.isArray(payload.strokes)) return null
  if (payload.strokes.length === 0 || payload.strokes.length > MAX_SIG_STROKES) return null
  let pointCount = 0
  const strokes: SignaturePoint[][] = []
  for (const rawStroke of payload.strokes) {
    if (!Array.isArray(rawStroke) || rawStroke.length === 0) return null
    pointCount += rawStroke.length
    if (pointCount > MAX_SIG_POINTS) return null
    const stroke: SignaturePoint[] = []
    for (const rawPoint of rawStroke) {
      if (!Array.isArray(rawPoint) || rawPoint.length !== 2) return null
      const [x, y] = rawPoint
      if (
        typeof x !== "number" ||
        typeof y !== "number" ||
        !Number.isFinite(x) ||
        !Number.isFinite(y) ||
        x < 0 ||
        x > 1 ||
        y < 0 ||
        y > 1
      ) {
        return null
      }
      stroke.push([x, y])
    }
    strokes.push(stroke)
  }
  return pointCount > 0 ? { v: 1, strokes } : null
}

export type FormAttachment = {
  mediaId: string
  url: string
  filename: string
  mimeType: string
  byteSize: number
  width?: number
  height?: number
}

export function parseAttachments(value: unknown): FormAttachment[] {
  if (typeof value !== "string" || value.length > 100_000) return []
  try {
    const payload = JSON.parse(value) as { v?: unknown; files?: unknown }
    if (payload.v !== 1 || !Array.isArray(payload.files)) return []
    return payload.files.filter((item): item is FormAttachment => {
      if (!item || typeof item !== "object") return false
      const f = item as Record<string, unknown>
      return (
        typeof f.mediaId === "string" &&
        typeof f.url === "string" &&
        /^https:\/\/files(?:-dev)?\.attend\.vip\//.test(f.url) &&
        typeof f.filename === "string" &&
        typeof f.mimeType === "string" &&
        typeof f.byteSize === "number"
      )
    })
  } catch {
    return []
  }
}
